/**
 * AdminChurchMember360 — Super-admin per-member dossier.
 *
 * Route: /admin/church-members/:memberId
 * Data: single call to GET /api/admin/church-members/{memberId}/360
 *
 * Mirrors the Person 360 layout but with church-specific dimensions
 * (giving history, ministries, attendance, family, home group).
 */
import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  ArrowLeft, UserCircle, Church, Wallet, HeartHandshake, Users, Home,
  Calendar, FileText, Bell, Layers, ExternalLink,
  Download, MessageSquare, HomeIcon, Crown, ShieldCheck,
} from 'lucide-react';
import { getToken } from '@/lib/authToken';

const API = process.env.REACT_APP_BACKEND_URL;
const authH = () => ({ Authorization: `Bearer ${getToken()}` });

const Stat = ({ label, value, tone = 'slate', icon: Icon, testid }) => {
  const tones = {
    slate: 'from-slate-500/10 to-slate-500/5 text-slate-300 border-slate-500/30',
    violet: 'from-violet-500/10 to-violet-500/5 text-violet-300 border-violet-500/30',
    emerald: 'from-emerald-500/10 to-emerald-500/5 text-emerald-300 border-emerald-500/30',
    amber: 'from-amber-500/10 to-amber-500/5 text-amber-300 border-amber-500/30',
    rose: 'from-rose-500/10 to-rose-500/5 text-rose-300 border-rose-500/30',
    sky: 'from-sky-500/10 to-sky-500/5 text-sky-300 border-sky-500/30',
  };
  return (
    <div data-testid={testid} className={`rounded-xl border bg-gradient-to-br p-4 ${tones[tone]}`}>
      <div className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-wider opacity-80">{label}</span>
        {Icon && <Icon className="h-4 w-4 opacity-70" />}
      </div>
      <div className="mt-2 text-2xl font-semibold">{value ?? '—'}</div>
    </div>
  );
};

export default function AdminChurchMember360() {
  const { memberId } = useParams();
  const navigate = useNavigate();
  const [messageOpen, setMessageOpen] = useState(false);
  const [hgOpen, setHgOpen] = useState(false);
  const [msgSubject, setMsgSubject] = useState('');
  const [msgBody, setMsgBody] = useState('');
  const [msgChannel, setMsgChannel] = useState('auto');
  const [selectedHg, setSelectedHg] = useState('');
  const [tplFilter, setTplFilter] = useState('all');
  const [statementDays, setStatementDays] = useState(365);
  const [statementCustomOpen, setStatementCustomOpen] = useState(false);
  const [customDays, setCustomDays] = useState(180);

  const q = useQuery({
    queryKey: ['church-member-360', memberId],
    queryFn: async () => {
      const r = await axios.get(`${API}/api/admin/church-members/${memberId}/360`, { headers: authH() });
      return r.data;
    },
    enabled: !!memberId,
  });

  const rolesQ = useQuery({
    queryKey: ['member-roles', memberId],
    queryFn: async () => {
      const churchId = q.data?.member?.church_id || q.data?.church?.id;
      if (!churchId) return { roles: [] };
      const r = await axios.get(
        `${API}/api/church/member-rbac/${churchId}/members/${memberId}/roles`, { headers: authH() });
      return r.data;
    },
    enabled: !!memberId && !!(q.data?.member?.church_id || q.data?.church?.id),
    staleTime: 0,
    refetchOnMount: 'always',
  });

  const homeGroupsQ = useQuery({
    queryKey: ['church-member-home-groups', memberId],
    queryFn: async () => {
      const r = await axios.get(
        `${API}/api/admin/church-members/${memberId}/home-groups/available`,
        { headers: authH() },
      );
      return r.data;
    },
    enabled: !!memberId && hgOpen,
  });

  const templatesQ = useQuery({
    queryKey: ['message-templates'],
    queryFn: async () => {
      const r = await axios.get(`${API}/api/admin/message-templates`, { headers: authH() });
      return r.data;
    },
    enabled: messageOpen,
  });

  const applyTemplate = (tpl) => {
    // Simple {{first_name}} / {{church_name}} substitution using the member/church context
    const m = q.data?.member || {};
    const church = q.data?.church || {};
    const subst = (s) => (s || '')
      .replaceAll('{{first_name}}', m.first_name || m.name || 'friend')
      .replaceAll('{{last_name}}', m.last_name || '')
      .replaceAll('{{church_name}}', church.name || 'our church');
    setMsgSubject(subst(tpl.subject));
    setMsgBody(subst(tpl.body));
    if (tpl.channel_hint) setMsgChannel(tpl.channel_hint);
  };

  const sendStatement = useMutation({
    mutationFn: async () => {
      const days = Math.max(7, Math.min(1825, Number(statementDays) || 365));
      const r = await axios.post(
        `${API}/api/admin/church-members/${memberId}/actions/send-statement?period_days=${days}`,
        {},
        { headers: authH(), responseType: 'blob' },
      );
      // Trigger download
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `giving_statement_${memberId}_${days}d.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      return { ok: true };
    },
    onSuccess: () => { toast.success('Giving statement downloaded'); q.refetch(); },
    onError: (e) => toast.error(`Statement failed: ${e?.response?.data?.detail || e.message}`),
  });

  const sendMessage = useMutation({
    mutationFn: async () => {
      const r = await axios.post(
        `${API}/api/admin/church-members/${memberId}/actions/message`,
        { subject: msgSubject, body: msgBody, channel: msgChannel },
        { headers: { ...authH(), 'Content-Type': 'application/json' } },
      );
      return r.data;
    },
    onSuccess: (data) => {
      toast.success(`Message queued · event ${(data.event_id || '').slice(-6)}`);
      setMessageOpen(false);
      setMsgSubject('');
      setMsgBody('');
      q.refetch();
    },
    onError: (e) => toast.error(`Message failed: ${e?.response?.data?.detail || e.message}`),
  });

  const assignHg = useMutation({
    mutationFn: async () => {
      const r = await axios.put(
        `${API}/api/admin/church-members/${memberId}/actions/home-group`,
        { home_group_id: selectedHg || null },
        { headers: { ...authH(), 'Content-Type': 'application/json' } },
      );
      return r.data;
    },
    onSuccess: () => {
      toast.success(selectedHg ? 'Home group assigned' : 'Home group cleared');
      setHgOpen(false);
      q.refetch();
    },
    onError: (e) => toast.error(`Assign failed: ${e?.response?.data?.detail || e.message}`),
  });

  if (q.isLoading) {
    return (
      <div className="min-h-screen bg-slate-950 p-6 text-slate-100" data-testid="church-member-360-loading">
        Loading member profile…
      </div>
    );
  }
  if (q.isError) {
    return (
      <div className="min-h-screen bg-slate-950 p-6 text-rose-300" data-testid="church-member-360-error">
        {q.error?.response?.data?.detail || q.error?.message || 'Failed to load'}
      </div>
    );
  }

  const data = q.data || {};
  const m = data.member || {};
  const church = data.church || {};
  const person = data.person;
  const cross = data.cross_workspace_memberships || [];
  const giving = data.giving_summary || {};
  const givingRecent = data.giving_recent || [];
  const attendance = data.attendance_summary || {};
  const ministries = data.ministries || [];
  const family = data.family || {};
  const homeGroup = data.home_group;
  const docs = data.documents || [];
  const notifs = data.notifications || [];
  const leadershipTimeline = data.leadership_timeline || [];
  const teamRoles = rolesQ.data?.roles || [];

  const displayName = `${m.first_name || ''} ${m.last_name || ''}`.trim() || 'Church Member';

  return (
    <div className="min-h-screen bg-slate-950 p-6 text-slate-100" data-testid="admin-church-member-360-page">
      <div className="mx-auto max-w-6xl space-y-6">
        {/* Header */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <Button
              data-testid="back-btn"
              variant="ghost"
              size="sm"
              className="mb-2 text-slate-400 hover:text-slate-100"
              onClick={() => navigate(-1)}
            >
              <ArrowLeft className="mr-1 h-4 w-4" /> Back
            </Button>
            <h1 className="flex items-center gap-3 text-3xl font-bold" data-testid="member-display-name">
              <UserCircle className="h-8 w-8 text-violet-400" />
              {displayName}
            </h1>
            <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-slate-400">
              {m.role && <Badge variant="outline" className="border-violet-500/40 text-violet-300">{m.role}</Badge>}
              {m.status && <Badge variant="outline" className={m.status === 'active' ? 'border-emerald-500/40 text-emerald-300' : 'border-slate-500/40 text-slate-300'}>{m.status}</Badge>}
              {m.email && <span data-testid="member-email">{m.email}</span>}
              {m.phone && <span data-testid="member-phone">· {m.phone}</span>}
            </div>
          </div>

          <div className="flex items-center gap-2">
            {church?.id && (
              <Button
                data-testid="church-link-btn"
                variant="outline"
                size="sm"
                className="border-slate-700 bg-slate-900 hover:bg-slate-800"
                onClick={() => navigate(`/church/${church.slug || church.id}`)}
              >
                <Church className="mr-1 h-4 w-4" /> {church.name || 'Church'}
              </Button>
            )}
            {church?.id && (
              <Button
                data-testid="hg-split-link"
                variant="outline"
                size="sm"
                className="border-amber-500/40 bg-amber-500/10 text-amber-300 hover:bg-amber-500/20"
                onClick={() => navigate(`/admin/churches/${church.id}/home-group-split`)}
              >
                <Home className="mr-1 h-4 w-4" /> Split View
              </Button>
            )}
            {person?.id && (
              <Button
                data-testid="person-link-btn"
                variant="outline"
                size="sm"
                className="border-violet-500/40 bg-violet-500/10 text-violet-300 hover:bg-violet-500/20"
                onClick={() => navigate(`/admin/persons/${person.id}`)}
              >
                <Layers className="mr-1 h-4 w-4" /> Person 360°
                <ExternalLink className="ml-1 h-3 w-3" />
              </Button>
            )}
          </div>
        </div>

        {/* Every office this person holds anywhere on the platform — iter 584 */}
        {(data.platform_offices?.office_count || 0) > 0 && (
          <Card className="border-slate-800 bg-slate-900/60" data-testid="platform-offices-card">
            <CardContent className="p-4 space-y-3">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs uppercase tracking-wider text-slate-400">
                  Offices across the platform
                </span>
                {Object.entries(data.platform_offices.by_module || {}).map(([mod, n]) => (
                  <Badge key={mod} variant="outline" className="border-slate-700 text-slate-300 text-[10px]">
                    {mod} · {n}
                  </Badge>
                ))}
              </div>
              <div className="divide-y divide-slate-800">
                {data.platform_offices.offices.map((o, i) => (
                  <div key={`${o.collection}-${o.record_id}-${i}`}
                       className="flex flex-wrap items-center gap-2 py-2"
                       data-testid={`platform-office-${i}`}>
                    <span className="text-sm text-slate-200">
                      {o.role || o.kind}{o.unit_name ? ` · ${o.unit_name}` : ''}
                    </span>
                    <span className="text-[11px] text-slate-500">
                      {o.module} · {o.kind}{o.since ? ` · since ${o.since}` : ''}
                    </span>
                    {o.status && (
                      <Badge variant="outline" className="ml-auto border-slate-700 text-slate-400 text-[10px] capitalize">
                        {o.status}
                      </Badge>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Cross-workspace membership badges — the ecosystem link */}
        {(cross && cross.length > 0) && (
          <Card className="border-violet-500/30 bg-violet-500/5" data-testid="cross-workspace-card">
            <CardContent className="flex flex-wrap items-center gap-3 p-4">
              <span className="text-xs uppercase tracking-wider text-violet-300">Also known in</span>
              {cross.map((c) => (
                <Badge
                  key={c.id}
                  data-testid={`cross-workspace-${c.workspace}`}
                  variant="outline"
                  className="border-violet-500/40 text-violet-200"
                >
                  {c.workspace} · {c.role_type} {c.role ? `(${c.role})` : ''}
                </Badge>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Actions row — Send Statement · Message · Assign Home Group */}
        <Card className="border-slate-800 bg-slate-900/60" data-testid="member-actions-card">
          <CardContent className="flex flex-col gap-3 p-4">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs uppercase tracking-wider text-slate-400">Statement period</span>
              {[
                { label: '3mo', days: 90 },
                { label: '6mo', days: 180 },
                { label: '12mo', days: 365 },
                { label: 'YTD', days: Math.max(1, Math.floor((Date.now() - new Date(new Date().getFullYear(), 0, 1).getTime()) / (1000 * 60 * 60 * 24))) },
              ].map((p) => (
                <button
                  key={p.label}
                  type="button"
                  data-testid={`statement-period-${p.label.toLowerCase()}`}
                  onClick={() => { setStatementDays(p.days); setStatementCustomOpen(false); }}
                  className={`rounded-full border px-3 py-1 text-xs transition-colors ${
                    !statementCustomOpen && statementDays === p.days
                      ? 'border-emerald-500 bg-emerald-500/15 text-emerald-200'
                      : 'border-slate-700 bg-slate-950 text-slate-400 hover:border-slate-600 hover:text-slate-200'
                  }`}
                >
                  {p.label}
                </button>
              ))}
              <button
                type="button"
                data-testid="statement-period-custom"
                onClick={() => setStatementCustomOpen((v) => !v)}
                className={`rounded-full border px-3 py-1 text-xs transition-colors ${
                  statementCustomOpen
                    ? 'border-emerald-500 bg-emerald-500/15 text-emerald-200'
                    : 'border-slate-700 bg-slate-950 text-slate-400 hover:border-slate-600 hover:text-slate-200'
                }`}
              >
                Custom
              </button>
              {statementCustomOpen && (
                <div className="flex items-center gap-1">
                  <Input
                    data-testid="statement-custom-days"
                    type="number"
                    min={7}
                    max={1825}
                    value={customDays}
                    onChange={(e) => {
                      const v = parseInt(e.target.value, 10) || 0;
                      setCustomDays(v);
                      setStatementDays(v);
                    }}
                    className="h-7 w-20 bg-slate-950 border-slate-700 text-xs"
                  />
                  <span className="text-xs text-slate-500">days</span>
                </div>
              )}
              <span className="ml-auto text-[10px] text-slate-500">
                Selected: {statementDays}d
              </span>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs uppercase tracking-wider text-slate-400">Actions</span>
              <Button
                data-testid="action-send-statement"
                size="sm"
                variant="outline"
                className="border-emerald-500/40 bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20"
                onClick={() => sendStatement.mutate()}
                disabled={sendStatement.isPending}
              >
                <Download className="mr-1 h-4 w-4" />
                {sendStatement.isPending ? 'Building PDF…' : `Send Statement (${statementDays}d)`}
              </Button>
              <Button
                data-testid="action-message"
                size="sm"
                variant="outline"
                className="border-sky-500/40 bg-sky-500/10 text-sky-300 hover:bg-sky-500/20"
                onClick={() => setMessageOpen(true)}
                disabled={!m.email && !m.phone}
              >
                <MessageSquare className="mr-1 h-4 w-4" />
                Message
              </Button>
              <Button
                data-testid="action-home-group"
                size="sm"
                variant="outline"
                className="border-amber-500/40 bg-amber-500/10 text-amber-300 hover:bg-amber-500/20"
                onClick={() => {
                  setSelectedHg(m.home_group_id || '');
                  setHgOpen(true);
                }}
              >
                <HomeIcon className="mr-1 h-4 w-4" />
                {homeGroup ? `Change home group (${homeGroup.name})` : 'Assign Home Group'}
              </Button>
              <span className="ml-auto text-xs text-slate-500">
                Every action is centrally audited via the Notification Log + Document Audit.
              </span>
            </div>
          </CardContent>
        </Card>

        {/* Message dialog */}
        <Dialog open={messageOpen} onOpenChange={setMessageOpen}>
          <DialogContent data-testid="message-dialog" className="bg-slate-900 border-slate-700 text-slate-100">
            <DialogHeader>
              <DialogTitle>Message {displayName}</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              {/* Templates library — one-click subject/body fill */}
              {(templatesQ.data?.items || []).length > 0 && (
                <div data-testid="msg-templates">
                  <div className="mb-2 flex items-center justify-between">
                    <div className="text-[10px] uppercase tracking-wider text-slate-500">
                      Templates ({templatesQ.data.items.length})
                    </div>
                    {/* Category filter row */}
                    <div className="flex flex-wrap gap-1" data-testid="msg-template-filters">
                      {(() => {
                        const cats = ['all', ...new Set((templatesQ.data.items || [])
                          .map((t) => t.category || 'other'))];
                        return cats.map((cat) => (
                          <button
                            key={cat}
                            type="button"
                            data-testid={`msg-template-filter-${cat}`}
                            onClick={() => setTplFilter(cat)}
                            className={`rounded-full border px-2 py-0.5 text-[10px] uppercase tracking-wider ${
                              tplFilter === cat
                                ? 'border-sky-500 bg-sky-500/15 text-sky-200'
                                : 'border-slate-800 text-slate-500 hover:text-slate-300'
                            }`}
                          >
                            {cat === 'all' ? 'all' : cat.replace('_', ' ')}
                          </button>
                        ));
                      })()}
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {(templatesQ.data.items || [])
                      .filter((tpl) => tplFilter === 'all' || (tpl.category || 'other') === tplFilter)
                      .map((tpl) => (
                      <button
                        type="button"
                        key={tpl.id}
                        data-testid={`msg-template-${tpl.key || tpl.id}`}
                        onClick={() => applyTemplate(tpl)}
                        className="rounded-full border border-slate-700 bg-slate-950 px-3 py-1 text-xs text-slate-300 hover:border-sky-500 hover:bg-sky-500/10 hover:text-sky-200"
                        title={`${tpl.category || 'other'} · ${tpl.subject}`}
                      >
                        {tpl.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex flex-wrap items-center gap-2 text-xs">
                <span className="uppercase text-slate-500">Channel</span>
                {['auto', 'email', 'sms', 'whatsapp'].map((ch) => (
                  <button
                    type="button"
                    key={ch}
                    onClick={() => setMsgChannel(ch)}
                    data-testid={`msg-channel-${ch}`}
                    className={`rounded-full border px-3 py-1 text-xs ${
                      msgChannel === ch
                        ? 'border-sky-500 bg-sky-500/20 text-sky-200'
                        : 'border-slate-700 bg-slate-950 text-slate-400 hover:text-slate-200'
                    }`}
                  >
                    {ch}
                  </button>
                ))}
              </div>
              <Input
                data-testid="msg-subject"
                value={msgSubject}
                onChange={(e) => setMsgSubject(e.target.value)}
                placeholder="Subject"
                className="bg-slate-950 border-slate-700"
              />
              <Textarea
                data-testid="msg-body"
                value={msgBody}
                onChange={(e) => setMsgBody(e.target.value)}
                placeholder="Message body…"
                className="min-h-[120px] bg-slate-950 border-slate-700"
              />
              <div className="flex justify-end gap-2">
                <Button
                  data-testid="msg-cancel"
                  variant="ghost"
                  onClick={() => setMessageOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  data-testid="msg-send"
                  className="bg-sky-600 hover:bg-sky-500"
                  onClick={() => sendMessage.mutate()}
                  disabled={sendMessage.isPending || !msgBody.trim()}
                >
                  {sendMessage.isPending ? 'Sending…' : 'Emit + Dispatch'}
                </Button>
              </div>
              <p className="text-[10px] text-slate-500">
                A Member_Message_Requested event is emitted. The notification engine dispatches
                via the recipient's preferred channel (or the one you selected).
              </p>
            </div>
          </DialogContent>
        </Dialog>

        {/* Home group dialog */}
        <Dialog open={hgOpen} onOpenChange={setHgOpen}>
          <DialogContent data-testid="home-group-dialog" className="bg-slate-900 border-slate-700 text-slate-100">
            <DialogHeader>
              <DialogTitle>Assign Home Group</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              {homeGroupsQ.isLoading ? (
                <div className="p-4 text-center text-sm text-slate-500">Loading…</div>
              ) : (
                <div className="space-y-1 max-h-72 overflow-y-auto">
                  <button
                    type="button"
                    data-testid="hg-option-clear"
                    onClick={() => setSelectedHg('')}
                    className={`w-full rounded border px-3 py-2 text-left text-sm ${
                      selectedHg === ''
                        ? 'border-amber-500 bg-amber-500/10 text-amber-200'
                        : 'border-slate-800 bg-slate-950 text-slate-400 hover:bg-slate-800'
                    }`}
                  >
                    — Clear assignment —
                  </button>
                  {(homeGroupsQ.data?.items || []).map((hg) => (
                    <button
                      type="button"
                      key={hg.id}
                      data-testid={`hg-option-${hg.id}`}
                      onClick={() => setSelectedHg(hg.id)}
                      className={`w-full rounded border px-3 py-2 text-left text-sm ${
                        selectedHg === hg.id
                          ? 'border-amber-500 bg-amber-500/10 text-amber-200'
                          : 'border-slate-800 bg-slate-950 text-slate-300 hover:bg-slate-800'
                      }`}
                    >
                      <div>{hg.name}</div>
                      {hg.city && <div className="text-xs text-slate-500">{hg.city}</div>}
                    </button>
                  ))}
                  {(homeGroupsQ.data?.items || []).length === 0 && !homeGroupsQ.isLoading && (
                    <p className="p-3 text-xs text-slate-500">
                      No home groups configured for this church yet. Create one under the
                      church hierarchy first.
                    </p>
                  )}
                </div>
              )}
              <div className="flex justify-end gap-2">
                <Button data-testid="hg-cancel" variant="ghost" onClick={() => setHgOpen(false)}>Cancel</Button>
                <Button
                  data-testid="hg-save"
                  className="bg-amber-600 hover:bg-amber-500"
                  onClick={() => assignHg.mutate()}
                  disabled={assignHg.isPending}
                >
                  {assignHg.isPending ? 'Saving…' : 'Save'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Stat row */}
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          <Stat testid="stat-giving-total" label="Total giving" value={
            giving.total ? `${giving.total.toLocaleString(undefined, { maximumFractionDigits: 0 })}` : '0'
          } tone="emerald" icon={Wallet} />
          <Stat testid="stat-giving-count" label="Contributions" value={giving.count || 0} tone="violet" icon={HeartHandshake} />
          <Stat testid="stat-attendance" label="Services attended" value={attendance.services_attended || 0} tone="sky" icon={Calendar} />
          <Stat testid="stat-ministries" label="Ministries" value={ministries.length} tone="amber" icon={Users} />
        </div>

        {/* Giving history */}
        <Card className="border-slate-800 bg-slate-900/60" data-testid="giving-history-card">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Wallet className="h-4 w-4 text-emerald-400" />
              Giving history · latest {givingRecent.length}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="mb-4 flex flex-wrap gap-2">
              {Object.entries(giving.by_category || {}).map(([cat, v]) => (
                <Badge
                  key={cat}
                  data-testid={`giving-cat-${cat}`}
                  variant="outline"
                  className="border-emerald-500/40 text-emerald-300"
                >
                  {cat} · {Number(v.total || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })} ({v.count})
                </Badge>
              ))}
              {Object.keys(giving.by_category || {}).length === 0 && (
                <span className="text-xs text-slate-500">No recorded giving.</span>
              )}
            </div>

            {givingRecent.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-xs uppercase tracking-wider text-slate-500">
                    <tr>
                      <th className="pb-2 text-left">Date</th>
                      <th className="pb-2 text-left">Category</th>
                      <th className="pb-2 text-left">Method</th>
                      <th className="pb-2 text-right">Amount</th>
                      <th className="pb-2 text-left">Status</th>
                    </tr>
                  </thead>
                  <tbody className="text-slate-300">
                    {givingRecent.map((t) => (
                      <tr key={t.id} data-testid={`giving-row-${t.id}`} className="border-t border-slate-800">
                        <td className="py-2 text-slate-400">{t.occurred_at ? new Date(t.occurred_at).toLocaleDateString() : '—'}</td>
                        <td className="py-2">{t.category || '—'}</td>
                        <td className="py-2 text-slate-400">{t.method || '—'}</td>
                        <td className="py-2 text-right font-medium">{t.currency} {Number(t.amount).toLocaleString(undefined, { maximumFractionDigits: 2 })}</td>
                        <td className="py-2"><span className="text-xs text-slate-500">{t.status}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-xs text-slate-500">No contributions found in the unified ledger.</p>
            )}
          </CardContent>
        </Card>

        {/* Ministries + Family + Home Group in a grid */}
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          <Card className="border-slate-800 bg-slate-900/60" data-testid="member-roles-card">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-base">
                <ShieldCheck className="h-4 w-4 text-emerald-400" /> Team roles
              </CardTitle>
            </CardHeader>
            <CardContent>
              {teamRoles.length > 0 ? (
                <ul className="space-y-2 text-sm text-slate-300">
                  {teamRoles.map((r) => (
                    <li key={r.id} data-testid={`member-role-${r.role_id}`}
                        className="rounded border border-slate-800 bg-slate-950 p-2">
                      <div className="font-medium">{r.role_name}</div>
                      <div className="mt-1 flex flex-wrap gap-1">
                        {(r.permissions || []).slice(0, 6).map((pk) => (
                          <span key={pk} className="rounded bg-slate-800 px-1.5 py-0.5 text-[9px] text-slate-400">
                            {pk.replace(/_/g, ' ')}
                          </span>
                        ))}
                      </div>
                      <div className="mt-1 text-[10px] text-slate-500">
                        since {r.assigned_at ? new Date(r.assigned_at).toLocaleDateString() : '—'}
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-xs text-slate-500">No team role assigned yet.</p>
              )}
            </CardContent>
          </Card>

          <Card className="border-slate-800 bg-slate-900/60" data-testid="ministries-card">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-base">
                <Users className="h-4 w-4 text-violet-400" /> Ministries
              </CardTitle>
            </CardHeader>
            <CardContent>
              {ministries.length > 0 ? (
                <ul className="space-y-2 text-sm text-slate-300">
                  {ministries.map((mn, i) => (
                    <li key={i} data-testid={`ministry-row-${i}`} className="rounded border border-slate-800 bg-slate-950 p-2">
                      <div className="font-medium">{mn.ministry_name}</div>
                      <div className="text-xs text-slate-500">{mn.role || 'member'} · since {mn.since || '—'}</div>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-xs text-slate-500">Not in any ministry yet.</p>
              )}
            </CardContent>
          </Card>

          <Card className="border-slate-800 bg-slate-900/60" data-testid="family-card">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-base">
                <UserCircle className="h-4 w-4 text-rose-400" /> Family
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 text-sm text-slate-300">
                {family.spouse && (
                  <div className="rounded border border-slate-800 bg-slate-950 p-2" data-testid="family-spouse">
                    <div className="text-xs text-slate-500">Spouse</div>
                    <div className="font-medium">{family.spouse.first_name} {family.spouse.last_name}</div>
                  </div>
                )}
                {(family.children || []).map((k, i) => (
                  <div key={i} className="rounded border border-slate-800 bg-slate-950 p-2" data-testid={`family-child-${i}`}>
                    <div className="text-xs text-slate-500">Child</div>
                    <div className="font-medium">{k.first_name} {k.last_name}</div>
                  </div>
                ))}
                {!family.spouse && !(family.children && family.children.length) && (
                  <p className="text-xs text-slate-500">No linked family members.</p>
                )}
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-800 bg-slate-900/60" data-testid="home-group-card">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-base">
                <Home className="h-4 w-4 text-amber-400" /> Home group
              </CardTitle>
            </CardHeader>
            <CardContent>
              {homeGroup ? (
                <div>
                  <div className="font-medium text-slate-200" data-testid="home-group-name">{homeGroup.name}</div>
                  <div className="text-xs text-slate-500">{homeGroup.city || ''} {homeGroup.country ? `· ${homeGroup.country}` : ''}</div>
                </div>
              ) : (
                <p className="text-xs text-slate-500">Not assigned to a home group.</p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Leadership Timeline — every office ever held (Legacy card) */}
        <Card className="border-amber-500/20 bg-slate-900/60" data-testid="leadership-timeline-card">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Crown className="h-4 w-4 text-amber-400" /> Leadership Legacy
              <Badge variant="outline" className="ml-2 border-slate-700 text-xs text-slate-400">
                {leadershipTimeline.length} office{leadershipTimeline.length === 1 ? '' : 's'}
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {leadershipTimeline.length === 0 ? (
              <p className="text-xs text-slate-500">
                No leadership appointments on record for this member.
              </p>
            ) : (
              <ol className="relative border-l border-amber-500/20 pl-4">
                {leadershipTimeline.map((it) => {
                  const active = ['active', 'acting'].includes(it.status);
                  return (
                    <li key={it.id} data-testid={`timeline-item-${it.id}`} className="mb-3 last:mb-0">
                      <span className={`absolute -left-[7px] mt-1 h-3 w-3 rounded-full border-2 border-slate-950 ${
                        active ? 'bg-emerald-400' : 'bg-slate-600'
                      }`} />
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-sm font-semibold text-slate-100">
                          {it.position_name}
                        </span>
                        <Badge variant="outline" className={
                          active ? 'border-emerald-500/40 text-emerald-300 text-[10px]'
                                 : 'border-slate-700 text-slate-400 text-[10px]'
                        }>
                          {it.status}
                          {it.is_acting && ' · acting'}
                        </Badge>
                        {it.level && (
                          <span className="text-[10px] uppercase tracking-wider text-slate-500">
                            {it.level.replace('_', ' ')}
                          </span>
                        )}
                      </div>
                      <div className="mt-0.5 text-[11px] text-slate-500">
                        {it.unit_name && <span>{it.unit_name} · </span>}
                        <span>
                          {it.start_date ? new Date(it.start_date).toLocaleDateString() : '—'}
                          {' → '}
                          {it.end_date ? new Date(it.end_date).toLocaleDateString() : (active ? 'present' : '—')}
                        </span>
                      </div>
                      {it.notes && (
                        <div className="mt-1 text-[11px] italic text-slate-400">"{it.notes}"</div>
                      )}
                    </li>
                  );
                })}
              </ol>
            )}
          </CardContent>
        </Card>

        {/* Documents produced for this member */}
        <Card className="border-slate-800 bg-slate-900/60" data-testid="documents-card">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <FileText className="h-4 w-4 text-emerald-400" /> Documents produced ({docs.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {docs.length > 0 ? (
              <table className="w-full text-sm">
                <thead className="text-xs uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="pb-2 text-left">When</th>
                    <th className="pb-2 text-left">Template</th>
                    <th className="pb-2 text-left">Module</th>
                    <th className="pb-2 text-left">Status</th>
                  </tr>
                </thead>
                <tbody className="text-slate-300">
                  {docs.map((d) => (
                    <tr key={d.id} data-testid={`doc-row-${d.id}`} className="border-t border-slate-800">
                      <td className="py-2 text-slate-400">{d.created_at ? new Date(d.created_at).toLocaleString() : '—'}</td>
                      <td className="py-2">{d.template}</td>
                      <td className="py-2 text-slate-400">{d.source_module}</td>
                      <td className="py-2">{d.success ? <span className="text-emerald-400">ok</span> : <span className="text-rose-400">failed</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="text-xs text-slate-500">No documents linked to this member yet.</p>
            )}
          </CardContent>
        </Card>

        {/* Notifications sent */}
        <Card className="border-slate-800 bg-slate-900/60" data-testid="notifications-card">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Bell className="h-4 w-4 text-sky-400" /> Notifications sent ({notifs.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {notifs.length > 0 ? (
              <table className="w-full text-sm">
                <thead className="text-xs uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="pb-2 text-left">When</th>
                    <th className="pb-2 text-left">Channel</th>
                    <th className="pb-2 text-left">Event</th>
                    <th className="pb-2 text-left">Recipient</th>
                    <th className="pb-2 text-left">Status</th>
                  </tr>
                </thead>
                <tbody className="text-slate-300">
                  {notifs.map((n) => (
                    <tr key={n.id} data-testid={`notif-row-${n.id}`} className="border-t border-slate-800">
                      <td className="py-2 text-slate-400">{n.created_at ? new Date(n.created_at).toLocaleString() : '—'}</td>
                      <td className="py-2">{n.channel}</td>
                      <td className="py-2 text-slate-400">{n.event_type}</td>
                      <td className="py-2 text-slate-400">{n.recipient}</td>
                      <td className="py-2">
                        <span className={n.status === 'sent' ? 'text-emerald-400' : n.status === 'failed' ? 'text-rose-400' : 'text-slate-400'}>
                          {n.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="text-xs text-slate-500">No notifications sent to this member yet.</p>
            )}
          </CardContent>
        </Card>

        <p className="text-center text-xs text-slate-500">
          <Layers className="mr-1 inline h-3 w-3" /> Reads only shared platform services · Member id {m.id}
        </p>
      </div>
    </div>
  );
}
